import React, { useState, useEffect } from 'react';
import { getEventTicketTypes } from '../services/eventTicketService';
import './EventTicketSelector.css';

const EventTicketSelector = ({
  event,
  onSelectionChange,
  onBack,
  restaurantName
}) => {
  const [ticketTypes, setTicketTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  const [quantity, setQuantity] = useState(0);
  const [feeConfig, setFeeConfig] = useState({ fixedFeeCents: 0, feePercent: 0 });

  useEffect(() => {
    const fetchTicketTypes = async () => {
      if (!event?.id) return;

      setLoading(true);
      setError('');
      try {
        const response = await getEventTicketTypes(event.id);
        // Handle both array and { data: [...] } response formats
        const types = Array.isArray(response) ? response : (response.data || []);
        setTicketTypes(types);

        // Extract fee config from response
        const fees = {
          fixedFeeCents: response.fixedFeeCents || 0,
          feePercent: response.feePercent || 0,
        };
        setFeeConfig(fees);

        // No initial selection — user must pick a ticket type
        if (onSelectionChange) {
          onSelectionChange([], 0, fees);
        }
      } catch (err) {
        console.error('Error fetching ticket types:', err);
        setError('Failed to load ticket types. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchTicketTypes();
  }, [event?.id]);

  const notifyParent = (type, qty) => {
    if (!onSelectionChange) return;
    if (!type || qty <= 0) {
      onSelectionChange([], 0, feeConfig);
      return;
    }
    const basePrice = type.priceCents / 100;
    const priceWithVat = type.priceExcludesVat ? basePrice * 1.14 : basePrice;
    const total = qty * priceWithVat;
    onSelectionChange([{
      ticketTypeId: type.id,
      ticketTypeName: type.name,
      quantity: qty,
      priceCents: type.priceCents,
      priceExcludesVat: type.priceExcludesVat
    }], total, feeConfig);
  };

  const handleSelectType = (typeId) => {
    const ticketType = ticketTypes.find(t => t.id === typeId);
    if (!ticketType) return;
    if (ticketType.remainingQuantity != null && ticketType.remainingQuantity === 0) return;

    const minQty = ticketType.minPerOrder || 1;
    setSelectedTypeId(typeId);
    setQuantity(minQty);
    notifyParent(ticketType, minQty);
  };

  const handleQuantityChange = (delta) => {
    const ticketType = ticketTypes.find(t => t.id === selectedTypeId);
    if (!ticketType) return;

    const minPerOrder = ticketType.minPerOrder || 1;
    const maxPerOrder = ticketType.maxPerOrder || 10;
    const newQty = Math.max(minPerOrder, quantity + delta);

    if (newQty > maxPerOrder) return;
    if (ticketType.remainingQuantity != null && newQty > ticketType.remainingQuantity) return;

    setQuantity(newQty);
    notifyParent(ticketType, newQty);
  };

  const calculateTotal = () => {
    const ticketType = ticketTypes.find(t => t.id === selectedTypeId);
    if (!ticketType || quantity <= 0) return 0;
    const basePrice = ticketType.priceCents / 100;
    const priceWithVat = ticketType.priceExcludesVat ? basePrice * 1.14 : basePrice;
    return quantity * priceWithVat;
  };

  const formatPrice = (cents, excludesVat = false) => {
    const price = cents / 100;
    if (excludesVat) {
      return `EGP ${price.toFixed(0)} + VAT`;
    }
    return `EGP ${price.toFixed(0)}`;
  };

  const formatDateTime = (dateStr, startTime, endTime) => {
    const date = new Date(dateStr);
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    const dateFormatted = date.toLocaleDateString('en-US', options);

    const formatTime = (time) => {
      if (!time) return '';
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    };

    let timeStr = '';
    if (startTime) {
      timeStr = formatTime(startTime);
    }

    return { dateFormatted, timeStr };
  };

  if (loading) {
    return (
      <div className="ticket-selector-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p>Loading ticket options...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ticket-selector-error">
        <i className="bi bi-exclamation-circle"></i>
        <p>{error}</p>
        <button className="btn btn-primary" onClick={() => window.location.reload()}>
          Try Again
        </button>
      </div>
    );
  }

  const { dateFormatted, timeStr } = formatDateTime(event.date, event.startTime, event.endTime);
  const totalPrice = calculateTotal();
  const selectedType = ticketTypes.find(t => t.id === selectedTypeId);

  return (
    <div className="event-ticket-selector">
      {/* Event Header */}
      <div className="event-header">
        {event.imageUrl && (
          <div className="event-image">
            <img src={event.imageUrl} alt={event.name} />
          </div>
        )}
        <div className="event-info">
          <h3 className="event-title">{event.name}</h3>
          <p className="event-venue">
            <i className="bi bi-geo-alt-fill"></i>
            {restaurantName}
          </p>
          <p className="event-datetime">
            <i className="bi bi-calendar-event"></i>
            {dateFormatted}
            {timeStr && <span className="event-time"> at {timeStr}</span>}
          </p>
          {event.description && (
            <p className="event-description">{event.description}</p>
          )}
        </div>
      </div>

      {/* Ticket Types */}
      <div className="ticket-types-section">
        <h4 className="section-title">
          <i className="bi bi-ticket-perforated"></i>
          Select Your Tickets
        </h4>

        {ticketTypes.length === 0 ? (
          <div className="no-tickets-available">
            <i className="bi bi-ticket-detailed"></i>
            <p>No tickets available for this event.</p>
          </div>
        ) : (
          <div className="ticket-types-list">
            {ticketTypes.map(type => {
              const isSoldOut = type.remainingQuantity != null && type.remainingQuantity === 0;
              const isSelected = selectedTypeId === type.id;
              const maxPerOrder = type.maxPerOrder || 10;
              const minPerOrder = type.minPerOrder || 1;
              const isMaxReached = isSelected && (quantity >= maxPerOrder ||
                (type.remainingQuantity != null && quantity >= type.remainingQuantity));
              const isMinReached = isSelected && quantity <= minPerOrder;

              return (
                <div
                  key={type.id}
                  className={`ticket-type-card ${isSoldOut ? 'sold-out' : ''} ${isSelected ? 'selected' : ''}`}
                  onClick={() => !isSoldOut && handleSelectType(type.id)}
                  role="radio"
                  aria-checked={isSelected}
                  tabIndex={isSoldOut ? -1 : 0}
                >
                  <div className="ticket-type-radio">
                    <span className={`radio-indicator ${isSelected ? 'active' : ''}`} />
                  </div>
                  <div className="ticket-type-info">
                    <div className="ticket-type-header">
                      <h5 className="ticket-type-name">{type.name}</h5>
                      <span className="ticket-type-price">
                        {formatPrice(type.priceCents, type.priceExcludesVat)}
                      </span>
                    </div>
                    {type.description && (
                      <p className="ticket-type-description">{type.description}</p>
                    )}
                    <div className="ticket-type-meta">
                      {isSoldOut && (
                        <span className="sold-out-badge">
                          <i className="bi bi-x-circle"></i>
                          Sold Out
                        </span>
                      )}
                      {type.minPerOrder > 1 && (
                        <span className="min-per-order-info">
                          Min {type.minPerOrder} per reservation
                        </span>
                      )}
                      {maxPerOrder && (
                        <span className="max-per-order-info">
                          Max {maxPerOrder} per reservation
                        </span>
                      )}
                    </div>

                    {isSelected && !isSoldOut && (
                      <div className="ticket-quantity-controls">
                        <button
                          className="qty-btn minus"
                          onClick={(e) => { e.stopPropagation(); handleQuantityChange(-1); }}
                          disabled={isMinReached}
                          aria-label="Decrease quantity"
                        >
                          <i className="bi bi-dash"></i>
                        </button>
                        <span className="qty-value">{quantity}</span>
                        <button
                          className="qty-btn plus"
                          onClick={(e) => { e.stopPropagation(); handleQuantityChange(1); }}
                          disabled={isMaxReached}
                          aria-label="Increase quantity"
                        >
                          <i className="bi bi-plus"></i>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Order Summary */}
      {selectedType && quantity > 0 && (() => {
        const subtotalCents = quantity * selectedType.priceCents;
        const feeCents = feeConfig.fixedFeeCents * quantity + Math.round(subtotalCents * feeConfig.feePercent / 100);
        const hasFee = feeCents > 0;
        const grandTotal = totalPrice + feeCents / 100;
        return (
          <div className="order-summary">
            <div className="summary-row">
              <span className="summary-label">{selectedType.name} x{quantity}</span>
              <span className="summary-value">EGP {totalPrice.toFixed(2)}</span>
            </div>
            {hasFee && (
              <div className="summary-row">
                <span className="summary-label">Processing Fee:</span>
                <span className="summary-value">EGP {(feeCents / 100).toFixed(2)}</span>
              </div>
            )}
            <div className="summary-row total">
              <span className="summary-label">Total Amount:</span>
              <span className="summary-value">EGP {(hasFee ? grandTotal : totalPrice).toFixed(2)}</span>
            </div>
            {selectedType.priceExcludesVat && (
              <p className="vat-note">
                <i className="bi bi-info-circle"></i>
                Price includes 14% VAT
              </p>
            )}
          </div>
        );
      })()}

      {/* Navigation */}
      <div className="ticket-selector-actions">
        {onBack && (
          <button className="btn btn-back" onClick={onBack}>
            <i className="bi bi-arrow-left"></i>
            Back
          </button>
        )}
      </div>
    </div>
  );
};

export default EventTicketSelector;
